import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useEnquiry } from "../context/EnquiryContext";

const LINKS = [
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/industries", label: "Industries" },
  { to: "/brands", label: "Brands" },
  { to: "/clients", label: "Clients" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.4 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 bg-paper border-b border-ink/15 transition-[padding] duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <div className="px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0">
            <img
              src="/jagdamba-logo.webp"
              alt="Jagdamba Enterprises"
              className={`mix-blend-multiply w-auto transition-[height] duration-500 ${
                scrolled ? "h-8" : "h-10 sm:h-12"
              }`}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
                    isActive ? "text-ink" : "text-ink/55 hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-send-requirement"
              onClick={() => openEnquiry("")}
              className="hidden sm:inline-flex items-center gap-2 bg-ink text-paper font-mono text-[11px] tracking-[0.18em] uppercase px-5 py-3 hover:bg-accent hover:text-ink transition-colors duration-300"
            >
              Send Your Requirement
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden p-2 -mr-2"
              aria-label="Open menu"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent origin-left"
          style={{ scaleX: progress }}
        />
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] bg-ink text-paper flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-paper/15">
              <span className="font-display font-black uppercase tracking-tight text-lg">
                Jagdamba Enterprises
              </span>
              <button
                data-testid="mobile-menu-close-button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="p-2"
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-1" aria-label="Mobile">
              {[{ to: "/", label: "Home" }, ...LINKS].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className="block font-display font-black uppercase tracking-tight text-4xl sm:text-5xl py-2 hover:text-accent transition-colors"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-10">
              <button
                data-testid="mobile-menu-cta-send-requirement"
                onClick={() => {
                  setMenuOpen(false);
                  openEnquiry("");
                }}
                className="w-full bg-accent text-ink font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 flex items-center justify-center gap-2"
              >
                Send Your Requirement <ArrowUpRight size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
